import React, { ForwardedRef, forwardRef, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectOption } from '@components/select';
import { COUNTRIES } from '@constants/countries';
import { Country } from '@constants/countries.type';
import { iso2FlagEmoji } from '@utils/iso-to-flag-emoji';
import classNames from 'classnames';

import { CountrySelectorProps } from './country-selector.type';

export const CountrySelector = memo(
  forwardRef(
    (
      {
        name,
        hasError,
        selectFontLight,
        required,
        initialCountry,
        id,
        disabled,
        readOnly,
        onChange,
        className,
        showDial,
        showSelectedLabel,
        countries,
        ...props
      }: CountrySelectorProps,
      ref: ForwardedRef<HTMLSelectElement>
    ) => {
      const { t } = useTranslation();
      const [selected, setSelected] = useState(initialCountry);
      const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelected(e.target.value);
        if (onChange) onChange(e);
      };

      const createLabel = useCallback(
        (option: Country) => `${iso2FlagEmoji(option.isoCode)} ${option.label} ${showDial ? option.dialCode : ''}`,
        [showDial]
      );

      const selectedCountry = selected ? (countries ?? COUNTRIES)[selected] : undefined;
      const closedLabel = selectedCountry
        ? [
            iso2FlagEmoji(selectedCountry.isoCode),
            showSelectedLabel ? selectedCountry.label : '',
            showDial ? selectedCountry.dialCode : '',
          ]
            .filter(Boolean)
            .join(' ')
        : '';

      // A native <select> shows its selected option's label and fires no event when its list closes
      // without a pick, so the closed box can't swap labels reliably. Options always carry the country
      // name (also what screen readers announce); the closed box hides the select's text and shows an
      // overlay with the flag instead.
      return (
        <div className={classNames('relative', { '[&_select]:text-transparent [&_option]:text-primary': closedLabel })}>
          <Select
            {...props}
            ref={ref}
            id={id}
            name={name}
            data-testid='select-country-input'
            hasError={hasError}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            selectFontLight={selectFontLight}
            value={selected}
            onChange={(e) => handleSelectChange(e as React.ChangeEvent<HTMLSelectElement>)}
            className={className}>
            {!selected && <SelectOption value='' label={t('common:selectCountryLabel')} disabled={!!selected} />}
            {React.useMemo(() => {
              return Object.values(countries ?? COUNTRIES).map((option) => (
                <SelectOption
                  key={option.isoCode}
                  data-testid={`select-country-option-${option.isoCode}`}
                  value={option.isoCode}
                  label={createLabel(option)}
                />
              ));
            }, [countries, createLabel])}
          </Select>
          {closedLabel && (
            <span
              aria-hidden
              data-testid='select-country-selected'
              className={classNames(
                'pointer-events-none absolute inset-y-0 left-0 right-8 flex items-center pl-3 text-primary',
                selectFontLight ? 'text-body-m' : 'text-subtitle-m',
                { 'opacity-50': readOnly }
              )}>
              <span className='truncate'>{closedLabel}</span>
            </span>
          )}
        </div>
      );
    }
  )
);
